import type { MouseEvent } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { HighlightedText } from '@/components/ui/highlighted-text';
import { cn } from '@/lib/utils';
import { Cell } from '@/modules/protocols/components/protocol-table-cells';
import { Icon, ProtocolButton } from '@/modules/protocols/components/ui';
import { protocolIcons } from '@/modules/protocols/constants';
import type { SOP } from '@/types';
import { sopTitle } from '../format';
import { SopState } from './sop-state';

interface ProtocolPickerColumnsOptions {
	/** The search's terms, marked wherever a row shows them. */
	terms: string[];
	onOpenMenu: (sop: SOP, event: MouseEvent<HTMLButtonElement>) => void;
}

const heading = (label: string, strong = false) => (
	<Cell>
		<span className={cn('text-xs leading-4 text-(--text-secondary)', strong && 'font-medium')}>{label}</span>
	</Cell>
);

/** The Protocols listing's columns, read off the SOP; Name and Category share the spare width (protocol-picker.css). */
export const getProtocolPickerColumns = ({ terms, onOpenMenu }: ProtocolPickerColumnsOptions): ColumnDef<SOP>[] => [
	{
		id: 'name',
		header: () => heading('Name', true),
		cell: ({ row }) => (
			<Cell>
				{/* No handler of its own: its click is the row's, so the keyboard opens the protocol too. */}
				<button type='button' className='min-w-0 cursor-pointer truncate text-left text-[13px] leading-4.5 text-(--text-body) capitalize'>
					<HighlightedText text={sopTitle(row.original.intent)} terms={terms} />
				</button>
			</Cell>
		)
	},
	{
		id: 'category',
		header: () => heading('Category'),
		cell: ({ row }) => {
			const { category, subcategory } = row.original;
			return (
				<Cell>
					{category ? (
						<span className='truncate text-[13px] leading-4.5 text-(--text-body)' title={[category, subcategory].filter(Boolean).join(' / ')}>
							<HighlightedText text={category} terms={terms} />
							{subcategory ? (
								<span className='text-(--text-secondary)'>
									{' / '}
									<HighlightedText text={subcategory} terms={terms} />
								</span>
							) : null}
						</span>
					) : (
						<span className='text-[13px] leading-4.5 text-(--text-secondary)'>Uncategorized</span>
					)}
				</Cell>
			);
		}
	},
	{
		id: 'status',
		size: 176,
		header: () => heading('Status'),
		cell: ({ row }) => (
			<Cell>
				<SopState sop={row.original} />
			</Cell>
		)
	},
	{
		id: 'actions',
		size: 46,
		header: () => <Cell />,
		cell: ({ row }) => (
			<Cell className='justify-end'>
				<span onClick={(event) => event.stopPropagation()}>
					<ProtocolButton
						variant='tertiary'
						size='icon'
						title='More'
						aria-label={`Actions for ${sopTitle(row.original.intent)}`}
						data-menu-trigger
						onClick={(event) => onOpenMenu(row.original, event)}
					>
						<Icon src={protocolIcons.headerEllipsis} size={14} />
					</ProtocolButton>
				</span>
			</Cell>
		)
	}
];
